//src/pages/gro/RiskInventoryFormPage.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useCreateRiskInventory } from "@/hooks/useRiskInventories";
import { useUnits } from "@/hooks/useUnits";
import type { RiskInventoryCreateIn } from "@/types/riskInventory";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyForm: RiskInventoryCreateIn = {
  unit_id: null,
  name: "",
  version_label: "",
};

export default function RiskInventoryFormPage() {
  const navigate = useNavigate();

  const createMut = useCreateRiskInventory();
  const unitsQ = useUnits();

  const units = unitsQ.data ?? [];

  const [form, setForm] = useState<RiskInventoryCreateIn>(emptyForm);
  const [saving, setSaving] = useState(false);

  function goBack() {
    navigate("/gro/risk-inventories");
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error("Informe o nome do inventário.");
      return;
    }

    setSaving(true);
    try {
      const created = await createMut.mutateAsync(form);
      toast.success("Inventário criado com sucesso.");
      navigate(`/gro/risk-inventories/${created.id}`);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao criar inventário.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div>
              <h1 className="text-xl font-bold tracking-tight">Novo Inventário</h1>
              <p className="text-sm text-muted-foreground">
                Cadastre um inventário manual para consolidar riscos e acompanhar ações.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={goBack} disabled={saving}>
              Cancelar
            </Button>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Salvar
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados do inventário</CardTitle>
        </CardHeader>

        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Nome *</Label>
            <Input
              className="mt-1"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Ex.: Inventário de Riscos 2026"
            />
          </div>

          <div>
            <Label>Unidade</Label>
            <Select
              value={form.unit_id ?? "__none__"}
              onValueChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  unit_id: value === "__none__" ? null : value,
                }))
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Sem unidade específica" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sem unidade específica</SelectItem>
                {units.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    {unit.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Versão / Rótulo</Label>
            <Input
              className="mt-1"
              value={form.version_label ?? ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  version_label: e.target.value,
                }))
              }
              placeholder="Ex.: 2026.1"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
